import { create } from 'zustand';
import { CitizenAlert, citizenAlertsData } from "./mockAlerts";
import { getApiUrl } from "@/utils/apiConfig";

const API_URL = getApiUrl();
const ALERTS_READ_KEY = "kaynin_read_alerts";

interface AlertsState {
  alerts: CitizenAlert[];
  isLoaded: boolean;
  readIds: string[];
  fetchAlerts: () => Promise<void>;
  addAlertInternal: (alert: CitizenAlert) => void;
  deleteAlertInternal: (id: string) => void;
  updateAlertInternal: (id: string, updates: Partial<CitizenAlert>) => void;
  markAsReadInternal: (id: string) => void;
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
  alerts: [],
  isLoaded: false,
  readIds: typeof window !== "undefined" ? JSON.parse(localStorage.getItem(ALERTS_READ_KEY) || "[]") : [],

  fetchAlerts: async () => {
    if (get().isLoaded) return;
    try {
      const res = await fetch(`${API_URL}/alerts.php`);
      const data = await res.json();
      if (Array.isArray(data)) {
        set({ alerts: data, isLoaded: true });
      }
    } catch (err) {
      console.error("Error fetching alerts:", err);
      // Fallback to mock if fetch fails
      set({ alerts: citizenAlertsData, isLoaded: true });
    }
  },

  addAlertInternal: (alert) => {
    set((state) => ({ alerts: [alert, ...state.alerts] }));
    fetch(`${API_URL}/alerts.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'add', alert })
    });
  },

  deleteAlertInternal: (id) => {
    set((state) => ({ alerts: state.alerts.filter(a => a.id !== id) }));
    fetch(`${API_URL}/alerts.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id })
    });
  },

  updateAlertInternal: (id, updates) => {
    set((state) => ({
      alerts: state.alerts.map(a => a.id === id ? { ...a, ...updates } : a)
    }));
    const updatedAlert = get().alerts.find(a => a.id === id);
    if (updatedAlert) {
      fetch(`${API_URL}/alerts.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update', id, alert: updatedAlert })
      });
    }
  },

  markAsReadInternal: (id) => {
    const { readIds } = get();
    if (!readIds.includes(id)) {
      const newReadIds = [...readIds, id];
      if (typeof window !== "undefined") {
        localStorage.setItem(ALERTS_READ_KEY, JSON.stringify(newReadIds));
      }
      set({ readIds: newReadIds });
    }
  }
}));

// Backwards compatibility wrappers
export function loadAllAlerts(): CitizenAlert[] {
  const store = useAlertsStore.getState();
  if (!store.isLoaded) {
    store.fetchAlerts();
    return []; // Return empty initially, it will trigger reactivity in useAlerts hook
  }
  return store.alerts;
}

export function saveAlerts(alerts: CitizenAlert[]) {
  // Not used directly to save array anymore, individual operations handle it
  // But to not break if something calls it:
  useAlertsStore.setState({ alerts });
}

export function addAlert(alert: Omit<CitizenAlert, "id" | "date">) {
  const dateIso = new Date().toISOString().slice(0, 10);
  const newAlert: CitizenAlert = {
    ...alert,
    id: `al-${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    date: dateIso
  };
  useAlertsStore.getState().addAlertInternal(newAlert);
  return newAlert;
}

export function deleteAlert(id: string) {
  useAlertsStore.getState().deleteAlertInternal(id);
}

export function updateAlert(id: string, updates: Partial<CitizenAlert>) {
  useAlertsStore.getState().updateAlertInternal(id, updates);
}

export function markAlertAsRead(id: string) {
  useAlertsStore.getState().markAsReadInternal(id);
}

export function getReadAlertIds(): string[] {
  return useAlertsStore.getState().readIds;
}
